import { Container, Graphics } from 'pixi.js';
import type { Palette } from '../palette/palettes';

type Orbiter = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  rotSpeed: number;
  orbitR: number;
  orbitSpeed: number;
  phase: number;
  baseSize: number;
  size: number;
  color: number;
  captured: boolean;
  absorbT: number;
  node: Container;
  gfx: Graphics;
};

export type LinkedInOrbitEvents = {
  onComplete: (originX: number, originY: number) => void;
};

function spinFromOrbit(vx: number, vy: number, dx: number, dy: number, dist: number): number {
  return (vx * -dy + vy * dx) / (dist * dist + 120);
}

/**
 * Gravity-well toy — click to spawn squares; mouse push adds momentum.
 */
export class LinkedInOrbit {
  readonly root = new Container();
  private hubGfx = new Graphics();
  private orbitersLayer = new Container();
  private orbiters: Orbiter[] = [];
  private center = { x: 0, y: 0 };
  private zone = { x: 0, y: 0, w: 0, h: 0 };
  private hubSize = 58;
  private hideOrbiters = false;
  private pointer = {
    x: 0,
    y: 0,
    pvx: 0,
    pvy: 0,
    active: false,
    inZone: false,
    overHub: false,
  };
  private lastPointerT = 0;
  private complete = false;
  private holdT = 0;
  private readonly holdDur = 0.38;
  private readonly maxOrbiters = 34;
  /** Visual weight of orbit squares — kept low so motion reads louder than presence. */
  private readonly orbiterAlpha = 0.42;
  private reducedMotion = false;

  constructor(
    private readonly palette: Palette,
    private readonly events: LinkedInOrbitEvents,
  ) {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.root.addChild(this.orbitersLayer, this.hubGfx);
  }

  layout(w: number, h: number): void {
    const mobile = w < 720;
    const narrow = w < 480;
    this.hideOrbiters = mobile;
    this.zone = {
      x: w * (narrow ? 0.04 : mobile ? 0.05 : 0.34),
      y: h * (narrow ? 0.36 : mobile ? 0.32 : 0.08),
      w: w * (mobile ? 0.92 : 0.58),
      h: h * (narrow ? 0.34 : mobile ? 0.38 : 0.62),
    };
    this.center = {
      x: this.zone.x + this.zone.w * 0.5,
      y: this.zone.y + this.zone.h * 0.46,
    };
    const hubMin = narrow ? 58 : mobile ? 54 : 52;
    this.hubSize = Math.max(hubMin, Math.min(76, Math.min(this.zone.w, this.zone.h) * (mobile ? 0.18 : 0.15)));
    this.orbitersLayer.visible = !this.hideOrbiters;
    this.spawnOrbiters();
    this.drawHub();
  }

  reset(): void {
    this.complete = false;
    this.holdT = 0;
    this.clearOrbiters();
    this.spawnOrbiters();
    this.drawHub();
  }

  setPointer(x: number, y: number, active: boolean): void {
    const now = performance.now();
    if (active && this.lastPointerT > 0) {
      const dt = Math.max(0.001, (now - this.lastPointerT) / 1000);
      this.pointer.pvx = (x - this.pointer.x) / dt;
      this.pointer.pvy = (y - this.pointer.y) / dt;
    } else if (!active) {
      this.pointer.pvx = 0;
      this.pointer.pvy = 0;
    }

    this.pointer.x = x;
    this.pointer.y = y;
    this.pointer.active = active;
    this.lastPointerT = now;
    this.pointer.inZone =
      x >= this.zone.x &&
      y >= this.zone.y &&
      x <= this.zone.x + this.zone.w &&
      y <= this.zone.y + this.zone.h;
    this.pointer.overHub = this.pointer.inZone && this.isOverHub(x, y);
  }

  /** Hub hit area — square, slightly larger than the drawn outline. */
  isOverHub(x: number, y: number): boolean {
    const half = this.hubSize * 0.56;
    return (
      Math.abs(x - this.center.x) <= half &&
      Math.abs(y - this.center.y) <= half
    );
  }

  /** Click in the orbit zone — spawn a new square that joins the swarm. */
  spawnAt(x: number, y: number): void {
    if (this.hideOrbiters || this.complete) return;
    if (this.orbiters.filter((o) => !o.captured).length >= this.maxOrbiters) return;
    if (Math.hypot(x - this.center.x, y - this.center.y) < this.hubSize * 0.65) return;

    const cx = this.center.x;
    const cy = this.center.y;
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.hypot(dx, dy) || 1;
    const orbitR = Math.min(this.zone.w * 0.46, Math.max(this.hubSize * 1.15, dist));
    const phase = Math.atan2(dy, dx);
    const colors = [this.palette.text, this.palette.receiver, this.palette.subtleB, this.palette.subtle];
    const color = colors[Math.floor(Math.random() * colors.length)]!;
    const size = this.randomOrbiterSize();
    const orbitSpeed = 0.5 + Math.random() * 0.32;
    const speed = 38 + Math.random() * 42;

    this.orbiters.push(
      this.makeOrbiter({
        x,
        y,
        vx: (-dy / dist) * speed + (Math.random() - 0.5) * 30,
        vy: (dx / dist) * speed + (Math.random() - 0.5) * 30,
        angle: phase * 0.4,
        rotSpeed: (Math.random() > 0.5 ? 1 : -1) * (1 + Math.random()),
        orbitR,
        orbitSpeed,
        phase,
        size,
        color,
      }),
    );
  }

  get origin(): { x: number; y: number } {
    return { ...this.center };
  }

  get pointerInZone(): boolean {
    return this.pointer.inZone;
  }

  get pointerOverHub(): boolean {
    return this.pointer.overHub;
  }

  /** Canvas-space orbit play area (squares + hub). */
  containsPoint(x: number, y: number): boolean {
    if (this.hideOrbiters) return false;
    return (
      x >= this.zone.x &&
      x <= this.zone.x + this.zone.w &&
      y >= this.zone.y &&
      y <= this.zone.y + this.zone.h
    );
  }

  private get allAbsorbed(): boolean {
    if (this.hideOrbiters) return true;
    const live = this.orbiters.filter((o) => !o.captured || o.size > 2.5);
    return live.length === 0 || live.every((o) => o.captured && o.size <= 2.5);
  }

  update(dt: number): void {
    if (this.complete) return;

    const cx = this.center.x;
    const cy = this.center.y;
    const boost = this.reducedMotion ? 0.55 : 1;
    const sucking = this.pointer.overHub;
    const t = performance.now() / 1000;

    const gravity = (sucking ? 420 : 58) * boost;
    const gravityRange = sucking ? this.zone.w * 0.88 : this.zone.w * 0.48;
    const resistance = sucking ? 0.992 : 0.988;
    const maxSpeed = (sucking ? 520 : 240) * boost;
    const resistancePow = Math.pow(resistance, dt * 60);
    const pointerSpeed = Math.hypot(this.pointer.pvx, this.pointer.pvy);

    if (!this.hideOrbiters) {
      for (const o of this.orbiters) {
        if (o.captured) {
          o.absorbT += dt;
          const pull = 1 - Math.pow(1 - Math.min(1, o.absorbT / 0.32), 3);
          o.x += (cx - o.x) * pull * 0.38;
          o.y += (cy - o.y) * pull * 0.38;
          o.size = Math.max(0, o.baseSize * (1 - pull * 0.92));
          o.vx *= 0.85;
          o.vy *= 0.85;
          o.rotSpeed +=
            spinFromOrbit(o.vx, o.vy, cx - o.x, cy - o.y, Math.hypot(cx - o.x, cy - o.y) || 1) * dt * 3;
          o.angle += o.rotSpeed * dt;
          this.syncOrbiterNode(o);
          continue;
        }

        const slotX = cx + Math.cos(o.phase + t * o.orbitSpeed) * o.orbitR;
        const slotY = cy + Math.sin(o.phase + t * o.orbitSpeed) * o.orbitR * 0.92;
        const orbitErr = Math.hypot(o.x - slotX, o.y - slotY);

        const dx = cx - o.x;
        const dy = cy - o.y;
        const dist = Math.hypot(dx, dy) || 1;
        const invDist = 1 / dist;
        const dirX = dx * invDist;
        const dirY = dy * invDist;

        if (sucking) {
          const vacuum = (1180 * boost) / (dist + 22);
          o.vx += dirX * vacuum * dt;
          o.vy += dirY * vacuum * dt;
          o.vx += dirX * gravity * dt;
          o.vy += dirY * gravity * dt;

          // Bleed off tangential drift so squares spiral in instead of skating past.
          const radial = o.vx * dirX + o.vy * dirY;
          const tanX = o.vx - dirX * radial;
          const tanY = o.vy - dirY * radial;
          const tanDamp = Math.exp(-10.5 * dt);
          o.vx = dirX * radial + tanX * tanDamp;
          o.vy = dirY * radial + tanY * tanDamp;
        } else {
          if (o.x > cx) o.vx -= gravity * dt;
          else o.vx += gravity * dt;
          if (o.y > cy) o.vy -= gravity * dt;
          else o.vy += gravity * dt;

          const tether = 26;
          o.vx += (slotX - o.x) * tether * dt;
          o.vy += (slotY - o.y) * tether * dt;
        }

        if (this.pointer.inZone && !sucking) {
          const mdx = this.pointer.x - o.x;
          const mdy = this.pointer.y - o.y;
          const md = Math.hypot(mdx, mdy) || 1;
          const mouseDistFromCenter = Math.hypot(this.pointer.x - cx, this.pointer.y - cy);
          const mouseOutsideRing = Math.abs(mouseDistFromCenter - o.orbitR) / (o.orbitR + 16);
          const stray = orbitErr / (o.orbitR * 0.45 + 12);
            const outsideFactor = Math.min(1.6, 0.25 + mouseOutsideRing * 0.85 + stray * 0.45);
            const pullR = this.zone.w * 0.52;

          if (md < pullR) {
            const proximity = 1 - md / pullR;
            const calm = Math.min(1, pointerSpeed / 160);
            const tug = (220 * boost * outsideFactor * proximity * (0.45 + calm * 0.4)) / (md + 32);
            o.vx += (mdx / md) * tug * dt;
            o.vy += (mdy / md) * tug * dt;

            if (pointerSpeed > 55) {
              const push = Math.min(1.2, pointerSpeed / 520) * proximity * 0.28;
              o.vx += this.pointer.pvx * push * dt;
              o.vy += this.pointer.pvy * push * dt;
            }

            o.rotSpeed += ((mdx * o.vy - mdy * o.vx) / (md * md + 300)) * outsideFactor * dt;
          }
        }

        const mousePulling = this.pointer.inZone && !sucking && pointerSpeed > 30;
        if (dist > gravityRange && !mousePulling) {
          o.vx *= resistancePow;
          o.vy *= resistancePow;
        }

        const spd = Math.hypot(o.vx, o.vy);
        if (spd > maxSpeed) {
          const s = maxSpeed / spd;
          o.vx *= s;
          o.vy *= s;
        }

        o.x += o.vx * dt;
        o.y += o.vy * dt;

        o.rotSpeed += spinFromOrbit(o.vx, o.vy, dx, dy, dist) * dt * 1.6;
        o.rotSpeed *= Math.exp(-1.8 * dt);
        o.angle += o.rotSpeed * dt;

        if (sucking && dist < this.hubSize * 0.98) {
          o.captured = true;
          o.absorbT = 0;
        }

        this.syncOrbiterNode(o);
      }
    }

    this.drawHub();

    if (this.allAbsorbed && this.pointer.overHub) {
      this.holdT += dt;
      const holdDur = this.reducedMotion ? 0.2 : this.holdDur;
      if (this.holdT >= holdDur) {
        this.complete = true;
        this.events.onComplete(cx, cy);
      }
    } else if (!this.pointer.overHub) {
      this.holdT = Math.max(0, this.holdT - dt * 2.5);
    }
  }

  private makeOrbiter(opts: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    angle: number;
    rotSpeed: number;
    orbitR: number;
    orbitSpeed: number;
    phase: number;
    size: number;
    color: number;
    alpha?: number;
  }): Orbiter {
    const gfx = new Graphics();
    gfx
      .rect(-opts.size * 0.5, -opts.size * 0.5, opts.size, opts.size)
      .fill({ color: opts.color, alpha: opts.alpha ?? this.orbiterAlpha });
    const node = new Container();
    node.addChild(gfx);
    this.orbitersLayer.addChild(node);
    return {
      ...opts,
      baseSize: opts.size,
      captured: false,
      absorbT: 0,
      node,
      gfx,
    };
  }

  /** Wide size spread — tiny flecks through chunky blocks. */
  private randomOrbiterSize(): number {
    const roll = Math.random();
    let scale: number;
    if (roll < 0.32) scale = 0.045 + Math.random() * 0.035;
    else if (roll < 0.68) scale = 0.08 + Math.random() * 0.055;
    else if (roll < 0.9) scale = 0.13 + Math.random() * 0.07;
    else scale = 0.19 + Math.random() * 0.11;
    return Math.max(4, this.hubSize * scale);
  }

  private clearOrbiters(): void {
    for (const o of this.orbiters) {
      o.node.destroy({ children: true });
    }
    this.orbiters = [];
    this.orbitersLayer.removeChildren();
  }

  private spawnOrbiters(): void {
    this.clearOrbiters();
    if (this.hideOrbiters) return;

    const count = this.reducedMotion ? 10 : 14;
    const colors = [this.palette.text, this.palette.receiver, this.palette.subtleB, this.palette.subtle];
    const cx = this.center.x;
    const cy = this.center.y;
    const maxR = Math.min(this.zone.w, this.zone.h) * 0.44;

    for (let i = 0; i < count; i++) {
      const orbitR = this.hubSize * (1.05 + Math.random() * 2.6) + (i % 6) * this.hubSize * 0.08;
      const clampedR = Math.min(maxR, orbitR);
      const phase = (i / count) * Math.PI * 2 + Math.random() * 0.85;
      const x = cx + Math.cos(phase) * clampedR;
      const y = cy + Math.sin(phase) * clampedR * (0.82 + Math.random() * 0.16);
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.hypot(dx, dy) || 1;
      const orbitSpeed = 0.48 + Math.random() * 0.34;
      const speed = (42 + Math.random() * 32 + (i % 5) * 6) * (this.reducedMotion ? 0.75 : 1);
      const size = this.randomOrbiterSize();

      this.orbiters.push(
        this.makeOrbiter({
          x,
          y,
          vx: (-dy / dist) * speed,
          vy: (dx / dist) * speed,
          angle: phase * 0.35,
          rotSpeed: (i % 2 === 0 ? 1 : -1) * (1.2 + (i % 4) * 0.35),
          orbitR,
          orbitSpeed,
          phase,
          size,
          color: colors[i % colors.length]!,
        }),
      );
    }
  }

  private syncOrbiterNode(o: Orbiter): void {
    if (o.size <= 2.5) {
      o.node.visible = false;
      return;
    }
    o.node.visible = true;
    o.node.position.set(o.x, o.y);
    o.node.rotation = o.angle;
    o.node.scale.set(o.size / o.baseSize);
  }

  private drawHub(): void {
    const { x, y } = this.center;
    const half = this.hubSize * 0.5;
    const sucking = this.pointer.overHub;
    const pulse = sucking ? 0.04 * Math.sin(performance.now() * 0.012) : 0;
    const strokeW = Math.max(2, this.hubSize * 0.045);
    const outlineAlpha = sucking ? 0.38 + Math.min(0.5, this.holdT * 1.4) : 0.36;

    this.hubGfx.clear();
    if (sucking) {
      this.hubGfx
        .rect(x - half, y - half, this.hubSize, this.hubSize)
        .fill({ color: this.palette.accent, alpha: 0.06 + Math.min(0.1, this.holdT * 0.25) });
    }
    this.hubGfx
      .rect(x - half - pulse, y - half - pulse, this.hubSize + pulse * 2, this.hubSize + pulse * 2)
      .stroke({
        width: strokeW,
        color: this.palette.accent,
        alpha: outlineAlpha,
      });
  }
}
